import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, clearRequestCache } from '../services/api.ts';

interface StoryStatus {
  status: string;
  current_step: string;
  has_final_video: boolean;
  character_phase: string;
  character_count: number;
  shots_count: number;
  final_url?: string;
}

interface UseStoryStatusReturn {
  status: StoryStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Global status cache to share across components
const statusCache = new Map<number, {
  data: StoryStatus | null;
  timestamp: number;
  subscribers: Set<() => void>;
  stableCount: number; // Track how many cycles without changes
  lastDataHash: string; // Hash of data to detect changes
}>();

const CACHE_DURATION = 500; // 0.5 second cache
const COMPLETED_CACHE_DURATION = 1000; // 1 second cache for completed stories

// Adaptive polling intervals for status
const STATUS_POLLING_INTERVALS = {
  ACTIVE: 2000,    // 2s when actively generating
  STABLE: 5000,    // 5s when stable for a few cycles
  IDLE: 10000,     // 10s when very stable
  MAX_IDLE: 30000  // 30s max when completely idle
};

// Helper to create status hash for change detection
const createStatusHash = (status: StoryStatus | null): string => {
  if (!status) return 'null';
  return JSON.stringify({
    status: status.status,
    current_step: status.current_step,
    has_final_video: status.has_final_video,
    character_phase: status.character_phase
  });
};

export const useStoryStatus = (storyId: number | null): UseStoryStatusReturn => {
  const [status, setStatus] = useState<StoryStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const subscribersRef = useRef<Set<() => void>>(new Set());

  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach(callback => callback());
  }, []);

  const fetchStatus = useCallback(async (force = false) => {
    console.log('useStoryStatus: fetchStatus called with force:', force, 'for story:', storyId);
    if (!storyId) {
      setStatus(null);
      return;
    }

    const now = Date.now();
    const cached = statusCache.get(storyId);

    // Use cache if available and not expired
    if (!force && cached) {
      // Use longer cache duration for completed stories
      const isCompleted = cached.data?.status === 'completed' && cached.data?.has_final_video;
      const cacheDuration = isCompleted ? COMPLETED_CACHE_DURATION : CACHE_DURATION;
      
      if ((now - cached.timestamp) < cacheDuration) {
        console.log('useStoryStatus: Using cached data for story:', storyId);
        setStatus(cached.data);
        return;
      }
    }

    try {
      if (firstLoad || force) setLoading(true);
      setError(null);

      console.log('useStoryStatus: Making API call with force:', force, 'for story:', storyId);
      const result = await apiGet(`/runs/${storyId}/final-status`, force);
      
      if (result.error) {
        setError(result.error);
        return;
      }

      const newStatus = result.data;
      setStatus(newStatus);

      // Check for status changes to determine stability
      const newDataHash = createStatusHash(newStatus);
      const cacheEntry = statusCache.get(storyId);
      const subscribers = cacheEntry?.subscribers || new Set();
      
      // Determine if status has changed
      const hasChanged = !cacheEntry || cacheEntry.lastDataHash !== newDataHash;
      const stableCount = hasChanged ? 0 : (cacheEntry?.stableCount || 0) + 1;
      
      statusCache.set(storyId, {
        data: newStatus,
        timestamp: now,
        subscribers,
        stableCount,
        lastDataHash: newDataHash
      });

      // Notify all global subscribers
      subscribers.forEach(callback => callback());
      
      // Notify local subscribers
      notifySubscribers();
      
      // Log polling status
      if (hasChanged) {
        console.log(`🔄 Status changed for story ${storyId}, resetting stability counter`);
      } else {
        console.log(`📊 Status stable for story ${storyId}, count: ${stableCount}`);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching story status:', err);
    } finally {
      if (firstLoad) setFirstLoad(false);
      setLoading(false);
    }
  }, [storyId, firstLoad, notifySubscribers]);

  const refetch = useCallback(() => {
    console.log('useStoryStatus: Full refresh for story:', storyId);
    // Clear the request cache to ensure fresh data
    clearRequestCache(`/runs/${storyId}/final-status`);
    // Force a fresh fetch
    fetchStatus(true);
  }, [fetchStatus, storyId]);

  // Subscribe to cache updates
  useEffect(() => {
    if (!storyId) {
      setStatus(null);
      return;
    }

    // Reset first load state when story changes
    setFirstLoad(true);

    const updateFromCache = () => {
      const cached = statusCache.get(storyId);
      if (cached) {
        setStatus(cached.data);
      }
    };

    // Add to subscribers
    const currentSubscribers = subscribersRef.current;
    currentSubscribers.add(updateFromCache);
    
    // Register with global cache subscribers
    let cacheEntry = statusCache.get(storyId);
    if (!cacheEntry) {
      cacheEntry = {
        data: null,
        timestamp: 0,
        subscribers: new Set(),
        stableCount: 0,
        lastDataHash: ''
      };
      statusCache.set(storyId, cacheEntry);
    }
    cacheEntry.subscribers.add(updateFromCache);
    
    // Clear cache for other stories to prevent stale data when switching
    const currentStoryId = storyId;
    statusCache.forEach((value, key) => {
      if (key !== currentStoryId) {
        statusCache.delete(key);
      }
    });
    
    // Get existing cache if available
    if (cacheEntry && cacheEntry.timestamp > 0) {
      setStatus(cacheEntry.data);
      setFirstLoad(false); // Data already available, not first load
    } else {
      // Fetch if no cache
      fetchStatus();
    }

    return () => {
      currentSubscribers.delete(updateFromCache);
      // Remove from global cache subscribers
      const entry = statusCache.get(storyId);
      if (entry) {
        entry.subscribers.delete(updateFromCache);
      }
    };
  }, [storyId, fetchStatus]);

  // Set up adaptive polling for active stories with visibility awareness
  useEffect(() => {
    if (!storyId || !status) return;

    // Only poll if story is not failed
    if (status.status === 'failed') {
      return;
    }
    
    // Stop polling only if story is completed AND has final video
    if (status.status === 'completed' && status.has_final_video) {
      console.log(`⏹️ Stopping status polling for story ${storyId} - completed with final video`);
      return;
    }
    
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isPolling = true;

    const getPollingInterval = (): number => {
      const cacheEntry = statusCache.get(storyId);
      if (!cacheEntry) return STATUS_POLLING_INTERVALS.ACTIVE;
      
      // Stop polling if completed with final video
      if (cacheEntry.data?.status === 'completed' && cacheEntry.data?.has_final_video) {
        return 0;
      }
      
      // Adaptive intervals based on stability
      if (cacheEntry.stableCount < 3) {
        return STATUS_POLLING_INTERVALS.ACTIVE; // 2s when actively changing
      } else if (cacheEntry.stableCount < 10) {
        return STATUS_POLLING_INTERVALS.STABLE; // 5s when stable for a few cycles
      } else if (cacheEntry.stableCount < 30) {
        return STATUS_POLLING_INTERVALS.IDLE; // 10s when very stable
      } else {
        return STATUS_POLLING_INTERVALS.MAX_IDLE; // 30s when completely idle
      }
    };

    const scheduleNextPoll = () => {
      if (!isPolling) return;
      
      const interval = getPollingInterval();
      if (interval === 0) {
        console.log(`⏹️ Stopping status polling for story ${storyId} - stop conditions met`);
        return;
      }
      
      timeoutId = setTimeout(async () => {
        if (document.visibilityState === 'visible' && isPolling) {
          await fetchStatus();
        }
        scheduleNextPoll();
      }, interval);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isPolling) {
        if (timeoutId) clearTimeout(timeoutId);
        fetchStatus(true).then(scheduleNextPoll);
      }
    };

    // Start polling
    scheduleNextPoll();
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      isPolling = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [storyId, status, fetchStatus]);

  return {
    status,
    loading,
    error,
    refetch
  };
};
